/**
 * Beams worked out for the notes that left the choice to the engraver.
 *
 * The document spells a beam four ways, and `auto` -- the value every note is written with
 * -- means "group this the way the metre says", not "no beam". MusicXML has no such value:
 * a note with no `<beam>` is a note with no beam, and a renderer that follows the file
 * literally, as Verovio does, draws four typed quavers with four flags. So the choice has
 * to be made somewhere, and this is where.
 *
 * It is made on the way out and only when the caller asks for it ({@link ExportOptions.beams}),
 * which keeps two things true at once: what a renderer receives is engraved, and what a user
 * saves is still the document's own word about its notes. A note explicitly beamed
 * `begin`/`continue`/`end` is written as it stands, and one explicitly `none` is left bare
 * -- deriving over either would overrule a decision someone made.
 *
 * Groups run to the beat and no further, which is the one rule every metre agrees on.
 * Compound metres count their beat as three of the written unit, so 6/8 beams in threes
 * rather than in the pairs a literal reading of `beat-type` would give.
 */

import { PPQ } from "../document/duration";
import type { NoteType } from "../document/duration";
import type { EventId } from "../document/ids";
import { capacityOf, occupiedTicks, resolveAttributes } from "../document/query";
import type { MusicEvent, Part, Score, TimeSignature, Voice } from "../document/types";

/** How MusicXML names the two ends of a beam, its middle, and a stub pointing either way. */
export type BeamValue = "begin" | "continue" | "end" | "forward hook" | "backward hook";

/** One beam on one note. `number` is the depth: 1 is the beam every quaver has. */
export interface BeamMark {
  readonly number: number;
  readonly value: BeamValue;
}

/** Beams a note of each written type carries when it is beamed at all. */
const FLAGS: Readonly<Record<NoteType, number>> = {
  whole: 0,
  half: 0,
  quarter: 0,
  eighth: 1,
  "16th": 2,
  "32nd": 3,
  "64th": 4,
  "128th": 5,
};

/** One note that may be beamed, and how deep its beaming goes. */
interface Candidate {
  readonly id: EventId;
  readonly flags: number;
}

/**
 * Ticks in one beat of a metre.
 *
 * A compound metre writes three units to the beat and beams them as one group: 6/8 is two
 * beats of three quavers, not six beats of one. Anything whose beat divides in two is read
 * straight off `beat-type`.
 */
function beatTicks(time: TimeSignature): number {
  const unit = (PPQ * 4) / time.beatType;
  const compound = time.beatType >= 8 && time.beats % 3 === 0;
  return compound ? unit * 3 : unit;
}

/** How deeply an event may be beamed, or 0 when it may not be. */
function depthOf(event: MusicEvent): number {
  if (event.kind !== "note" || event.grace !== null || event.beam !== "auto") return 0;
  return FLAGS[event.duration.type];
}

function add(into: Map<EventId, BeamMark[]>, id: EventId, mark: BeamMark): void {
  const existing = into.get(id);
  if (existing === undefined) into.set(id, [mark]);
  else existing.push(mark);
}

/**
 * One unbroken stretch of notes deep enough for one level of beam.
 *
 * Two or more of them carry a beam between them. A single one carries a hook instead -- the
 * half-beam of a dotted quaver and semiquaver -- which points back at the note it follows,
 * and forward only when there is no note behind it in the group to point at.
 */
function spanLevel(
  group: readonly Candidate[],
  from: number,
  to: number,
  level: number,
  into: Map<EventId, BeamMark[]>,
): void {
  if (from === to) {
    const lone = group[from];
    if (level > 1 && lone !== undefined) {
      add(into, lone.id, { number: level, value: from > 0 ? "backward hook" : "forward hook" });
    }
    return;
  }
  for (let at = from; at <= to; at += 1) {
    const item = group[at];
    if (item === undefined) continue;
    const value: BeamValue = at === from ? "begin" : at === to ? "end" : "continue";
    add(into, item.id, { number: level, value });
  }
}

/**
 * Every beam of one group, level by level.
 *
 * The first beam spans the whole group -- every member of it is a quaver or shorter, or it
 * would not be in the group. Each deeper level spans only the stretches of notes short
 * enough to have it, which is what makes a quaver between two semiquavers break the second
 * beam without breaking the first.
 */
function markGroup(group: readonly Candidate[], into: Map<EventId, BeamMark[]>): void {
  if (group.length < 2) return;
  const deepest = Math.max(...group.map((item) => item.flags));

  for (let level = 1; level <= deepest; level += 1) {
    let start = 0;
    while (start < group.length) {
      if ((group[start]?.flags ?? 0) < level) {
        start += 1;
        continue;
      }
      let end = start;
      while (end + 1 < group.length && (group[end + 1]?.flags ?? 0) >= level) end += 1;
      spanLevel(group, start, end, level, into);
      start = end + 1;
    }
  }
}

/**
 * The groups of one voice of one bar.
 *
 * A group ends wherever the music stops being beamable: at a rest, at a note too long to
 * beam, at a grace note, at a note whose beam was set by hand, and at every beat line. A
 * note that straddles a beat line is left out of both groups rather than joined to either,
 * because a beam drawn across it would say the beat falls somewhere it does not.
 */
function markVoice(
  voice: Voice,
  beat: number,
  capacity: number,
  into: Map<EventId, BeamMark[]>,
): void {
  let clock = 0;
  let group: Candidate[] = [];
  let beatOf = -1;

  for (const event of voice.events) {
    const ticks = occupiedTicks(event, capacity);
    const flags = depthOf(event);
    const within = Math.floor(clock / beat);
    const fits = clock + ticks <= (within + 1) * beat;

    if (!(flags > 0 && fits && within === beatOf)) {
      markGroup(group, into);
      group = [];
    }
    if (flags > 0 && fits) {
      group.push({ id: event.id, flags });
      beatOf = within;
    } else {
      beatOf = -1;
    }
    clock += ticks;
  }

  markGroup(group, into);
}

/** Where a beam falls on each note of a part that left its beaming to be worked out. */
export function beamMarks(score: Score, part: Part): ReadonlyMap<EventId, readonly BeamMark[]> {
  const marks = new Map<EventId, BeamMark[]>();

  score.measures.forEach((global, index) => {
    const measure = part.measures[global.id];
    if (measure === undefined) return;
    const beat = beatTicks(resolveAttributes(score, index).time);
    const capacity = capacityOf(score, index);
    for (const voice of measure.voices) markVoice(voice, beat, capacity, marks);
  });

  return marks;
}
